// src/components/stages/AilmentAssessmentEditor.tsx
// The ailment template's own dynamic sections (everything an admin authors
// per-module in the Ailment Templates builder — Diabetes Status, Last HbA1c,
// Complications, Diet Assessment, Physical Activity, the screening Due
// Checklist, and so on) — extracted out of PreConsultStage.tsx so the exact
// same rendering (same field controls, same due-checklist badges/ring, same
// numeric-field trend sparkline) is available to ConsultStage.tsx's
// doctor-facing Pre-Consult correction form, not a hand-built lookalike.
// PreConsultStage.tsx itself now renders through this component too, so
// there is one implementation of "what an ailment template question looks
// like," not two that can silently drift.
import { useEffect, useState } from 'react';
import VoiceMicButton from '../VoiceMicButton';
import PatientHistoryPanel from '../PatientHistoryPanel';
import { SegmentedControl } from '../ui/SegmentedControl';
import { ChoiceMatrix } from '../ui/ChoiceMatrix';
import { PictorialChoiceList } from '../ui/PictorialChoiceList';
import { DonutStat } from '../ui/DonutStat';
import { Badge } from '../ui/Badge';
import { SectionCard, type SectionStatus } from '../ui/SectionCard';
import { TrendSparkline, type TrendPoint } from '../ui/TrendSparkline';
import { getAssessmentTrend } from '../../api';
import { dueChecklistStatus, isDue, DUE_STATUS_BADGE } from '../../lib/dueChecklist';
import { isFieldVisible } from '../../utils/conditions';
import { activeBandIndex, bandLabels, type NormalisedField, type NormalisedSection } from '../../lib/assessmentTemplate';
import { cn } from '../../lib/cn';

export type FieldValue = string | string[] | Record<string, string> | undefined;

export type ConditionStatus = 'No' | 'Yes' | 'Unknown';
// Fixed colours matching backend/utils/assessmentScoring.js's
// CONDITION_STATUS_COLORS exactly — the scale a nurse sets here can never
// disagree with the colour the server scores it as.
const CONDITION_STATUS_OPTIONS: ReadonlyArray<{ value: ConditionStatus; label: string; tone?: 'success' | 'danger' }> = [
  { value: 'No', label: 'No', tone: 'success' },
  { value: 'Yes', label: 'Yes', tone: 'danger' },
  { value: 'Unknown', label: 'Unknown' },
];

function formatDueDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** " · 3 months ago" — how stale a prior reading is, which is most of what a
 * date is being read for here. Empty rather than "0 months ago" inside the
 * first month, and empty for an unparseable or future date. */
function agoLabel(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days < 30) return '';
  const months = Math.floor(days / 30);
  if (months < 12) return ` · ${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(months / 12);
  return ` · ${years} year${years === 1 ? '' : 's'} ago`;
}

const BAND_TONE: Record<'green' | 'yellow' | 'red', 'success' | 'warn' | 'danger'> = {
  green: 'success', yellow: 'warn', red: 'danger',
};
const BAND_TEXT: Record<'green' | 'yellow' | 'red', string> = {
  green: 'text-success-ink', yellow: 'text-warn-ink', red: 'text-danger-ink',
};

/** "Recent" under ~3 months old; "Stale" beyond the field's own declared
 * interval, when it has one — a numeric field with no interval can still
 * read Recent but never Stale, since there is nothing to be stale against.
 * Nothing shown in the ambiguous middle (past ~3 months, short of a longer
 * declared interval) rather than guessing. Pre-Consult redesign Phase 5. */
function recencyBadge(asOfIso: string, intervalMonths: number | undefined): { label: string; tone: 'success' | 'danger' } | null {
  const then = new Date(asOfIso).getTime();
  if (Number.isNaN(then)) return null;
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days < 0) return null; // a future date is unparseable-adjacent, not "recent"
  if (days <= 90) return { label: 'Recent', tone: 'success' };
  if (intervalMonths && days > intervalMonths * 30) return { label: 'Stale', tone: 'danger' };
  return null;
}

// A numeric template field, with the reading it holds shown at a size worth
// reading and placed on the template's own scoring scale — the Pre-Consult
// wireframe's "LAST HbA1c" card. Value-first (Phase 5): the oversized
// reading and its date lead, the plain input sits below it as the actual
// entry control — the input is for typing, the big number is for reading
// across a desk, and neither replaces the other.
//
// Entirely generic: the bands, their labels and their colours all come from
// the field's declared `thresholds`, so every numeric field in every module
// gets this, and editing a threshold in the Admin Panel relabels the scale.
// A field with no thresholds is just an input and its trend — nothing is
// invented for it.
function NumericField({ field, value, onChange, trend, asOfDate }: {
  field: NormalisedField;
  value: string | undefined;
  onChange: (v: FieldValue) => void;
  trend?: TrendPoint[];
  /** When this value was recorded — the Pre-Consult stage's own completion
   * time once saved, or "now" while still being entered live. Drives the
   * recency Badge; absent (e.g. a caller with no stage timing at all)
   * simply shows no badge rather than guessing. */
  asOfDate?: string;
}) {
  const bands = field.thresholds;
  const activeBand = activeBandIndex(bands, value);
  const labels = bandLabels(bands, field.suffix);
  const activeColor = activeBand >= 0 ? bands[activeBand]!.color : null;

  const history = trend ?? [];
  const previous = history[history.length - 1];
  // Today's reading completes the series, exactly as the wireframe draws it
  // ("8.2 → 7.6 → 7.0", where 7.0 is the value on screen). Without this a
  // trend needs two prior visits before it says anything, and the reading the
  // nurse is actually looking at sits outside its own trend line. It carries
  // no `recorded_at` — it is not recorded yet.
  const currentPoint: TrendPoint | null =
    value !== undefined && value !== '' && Number.isFinite(Number(value))
      ? { value: Number(value), color: activeColor }
      : null;
  const series = currentPoint ? [...history, currentPoint] : history;
  const recency = currentPoint && asOfDate ? recencyBadge(asOfDate, field.interval_months) : null;

  return (
    <>
      {/* The entered reading, restated large and in its band's colour —
          leads the card now, above the input. */}
      {currentPoint && (
        <div className="mb-2 flex items-end gap-2.5">
          <div>
            <div className={cn('text-[32px] font-extrabold leading-none', activeColor ? BAND_TEXT[activeColor] : 'text-ink')}>
              {value}
              {field.suffix && <span className="text-[13px] font-semibold"> {field.suffix}</span>}
            </div>
            {asOfDate && <div className="mt-1 text-[10.5px] text-ink-muted">{formatDueDate(asOfDate)}</div>}
          </div>
          {recency && <Badge variant={recency.tone === 'success' ? 'success' : 'danger'}>{recency.label}</Badge>}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <input type="number" value={value || ''} onChange={e => onChange(e.target.value)} style={{ maxWidth: 110 }} />
        {!value && field.suffix && <span style={{ fontSize: 12, color: 'var(--color-ink-muted)' }}>{field.suffix}</span>}
        {/* What this reading is being compared against. Shown even before
            anything is typed, so the nurse can see the previous value while
            deciding what to enter. */}
        {previous && (
          <span className="text-[11.5px] text-ink-muted">
            previous <b className="font-semibold text-ink">{previous.value}{field.suffix ? ` ${field.suffix}` : ''}</b>
            {previous.recorded_at ? ` · ${formatDueDate(previous.recorded_at)}${agoLabel(previous.recorded_at)}` : ''}
          </span>
        )}
      </div>

      {bands.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
          {/* Read-only: this states where the value sits, it is not another
              way to set it. Same track chrome as SegmentedControl — opaque
              control-line, joined segments — so a scale you read and a scale
              you set look like the same kind of object. */}
          <div className="inline-flex items-stretch overflow-hidden rounded-[16px] border border-control-line bg-card [&>*+*]:border-l [&>*+*]:border-control-line">
            {labels.map((label, i) => {
              const band = bands[i]!;
              const on = i === activeBand;
              const tone = BAND_TONE[band.color];
              return (
                <span
                  key={label + i}
                  className={cn(
                    'flex min-h-[26px] items-center px-2.5 text-[11px] font-semibold',
                    on
                      ? tone === 'success' ? 'bg-success-soft text-success-ink'
                        : tone === 'warn' ? 'bg-warn-soft text-warn-ink'
                        : 'bg-danger-soft text-danger-ink'
                      : 'text-ink-muted'
                  )}
                >
                  {/* A ✓ on the active band — Phase 5 — so the scale doesn't
                      rely on background tint alone to say "you are here". */}
                  {on ? `✓ ${label}` : label}
                </span>
              );
            })}
          </div>
        </div>
      )}

      <TrendSparkline points={series} unit={field.suffix} />
    </>
  );
}

// The screening due-checklist — Pre-Consult redesign Phase 4. A two-column
// table (Screening · Last done · Status) rather than one long list, "—" for
// never-done rather than a blank, and a Done/Due/Pending DonutStat instead
// of the old two-tone ring. Its own component (not inline in TemplateField)
// because it needs its own `useState` for which row's date editor is open —
// a hook can't live inside TemplateField's conditional branches.
//
// dueChecklistStatus/isDue/DUE_STATUS_BADGE are unchanged — this is
// presentation only, and `screeningsDue` (PreConsultStage.tsx) keeps
// computing off the same items/answers shape it always has.
function DueChecklistField({ field, value, onChange, disabled }: {
  field: NormalisedField;
  value: FieldValue;
  onChange: (v: FieldValue) => void;
  disabled?: boolean;
}) {
  // Guarded against an already-corrupted stored value (a plain string, from
  // before utils/extractedFields.js validated this shape server-side) —
  // spreading one below would otherwise turn its characters into numeric
  // keys. See extractedFields.js's header comment for how this was found.
  const answersByLabel = (value && typeof value === 'object' && !Array.isArray(value))
    ? (value as Record<string, { last_done?: string }>)
    : {};
  const rows = field.items.map(item => {
    const lastDone = answersByLabel[item.label]?.last_done || '';
    return { item, lastDone, status: dueChecklistStatus(lastDone, item.interval_months) };
  });
  const doneCount = rows.filter(r => r.status === 'green').length;
  const overdueCount = rows.filter(r => r.status === 'red').length;
  const pendingCount = rows.filter(r => r.status === 'yellow').length;
  const dueCount = rows.filter(r => isDue(r.status)).length;

  // Two columns, mockup-style, rather than one long list — split as evenly
  // as the count allows; an empty second column (≤1 item) is simply omitted.
  const mid = Math.ceil(rows.length / 2);
  const columns = [rows.slice(0, mid), rows.slice(mid)].filter(col => col.length > 0);

  const setLastDone = (label: string, dateStr: string) => {
    onChange({ ...answersByLabel, [label]: { last_done: dateStr } } as FieldValue);
  };

  return (
    <div className="mt-1">
      <div className="flex flex-wrap items-start gap-6">
        {/* Deliberately no min-w-0 here: that would let this block shrink
            below its tables' own min-width instead of the outer row
            wrapping, which is what let the donut end up squeezed beside a
            collapsed, overflowing table on a narrow screen. */}
        <div className="flex min-w-[220px] flex-1 flex-wrap gap-x-8 gap-y-3">
          {columns.map((col, ci) => (
            <table key={ci} className="min-w-[220px] flex-1 border-collapse">
              <thead>
                <tr>
                  <th className="pb-1.5 text-left text-[9.5px] font-bold uppercase tracking-wide text-ink-muted">Screening</th>
                  <th className="pb-1.5 text-left text-[9.5px] font-bold uppercase tracking-wide text-ink-muted">Last done</th>
                  <th className="pb-1.5 text-left text-[9.5px] font-bold uppercase tracking-wide text-ink-muted">Status</th>
                </tr>
              </thead>
              <tbody>
                {col.map(({ item, lastDone, status }) => (
                  <tr key={item.label} className="border-t border-line">
                    <td className="py-1.5 pr-2 text-[12px] text-ink">{item.label}</td>
                    <td className="py-1.5 pr-2 text-[12px]">
                      {/* A real date input, always present, so its native
                          calendar icon opens the picker on one click — the
                          previous click-to-reveal button meant a first click
                          just swapped in the input and a second click was
                          needed to actually open the picker. showPicker()
                          also lets a click anywhere on the row's date cell
                          (not just the tiny native icon) open the calendar. */}
                      <input
                        type="date"
                        disabled={disabled}
                        value={lastDone}
                        onChange={e => setLastDone(item.label, e.target.value)}
                        onClick={e => { if (!disabled && 'showPicker' in e.currentTarget) { try { e.currentTarget.showPicker(); } catch { /* unsupported browser — native click behavior still works */ } } }}
                        style={{ fontSize: 11, width: 132 }}
                      />
                    </td>
                    <td className="py-1.5">
                      <Badge variant={DUE_STATUS_BADGE[status]}>
                        {status === 'red' ? '⚠ Due' : status === 'yellow' ? '⏳ Due soon' : '✓ Done'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
        </div>

        <div className="flex shrink-0 flex-col items-center gap-2">
          <div className="text-[10.5px] font-bold uppercase tracking-wide text-ink-muted">Screening summary</div>
          <div className="flex items-center gap-3">
            <DonutStat
              segments={[
                { value: overdueCount, tone: 'danger' },
                { value: pendingCount, tone: 'warn' },
                { value: doneCount, tone: 'success' },
              ]}
              total={rows.length}
              centerValue={dueCount}
              centerLabel="due"
            />
            <div className="flex flex-col gap-1 text-[11px]">
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 shrink-0 rounded-sm bg-success-ink" />Done {doneCount}</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 shrink-0 rounded-sm bg-danger-ink" />Due {overdueCount}</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 shrink-0 rounded-sm bg-warn-ink" />Pending {pendingCount}</span>
            </div>
          </div>
        </div>
      </div>

      {field.footnote && (
        <div className="mt-2.5 rounded-lg bg-info-soft px-2.5 py-1.5 text-[11px] text-info-ink">ⓘ {field.footnote}</div>
      )}
    </div>
  );
}

// Renders one dynamic field from a normalised template section. Every shape
// question (missing options, string-vs-object items, an unknown type) was
// already settled by normaliseTemplate above, so this only decides layout.
export function TemplateField({ field, value, onChange, patientUhid, trend, disabled, historyRefreshToken }: {
  field: NormalisedField;
  value: FieldValue;
  onChange: (v: FieldValue) => void;
  patientUhid: string | undefined;
  /** This field's prior readings, oldest first. Only ever populated for
   * numeric fields; absent or empty renders nothing. */
  trend?: TrendPoint[];
  /** See SegmentedControl's own doc comment — its selected option would
   * otherwise fade along with everything else inside a disabled
   * `<fieldset>`. Plain inputs/textareas here need no equivalent prop; the
   * fieldset already covers them. */
  disabled?: boolean;
  /** Reloads a history-table field — PreConsultStage bumps it after its
   * "Pull from patient record via AI" seeds the history tables. */
  historyRefreshToken?: unknown;
}) {
  if (field.type === 'history-table') {
    return <PatientHistoryPanel kind={field.history_source!} uhid={patientUhid} editable compact refreshToken={historyRefreshToken} />;
  }
  // A choice field whose options an admin has emptied would render as a label
  // with nothing under it — unanswerable, and it would hold the section at
  // "in progress" for ever. Fall back to free text so the nurse can still
  // record something, and say why. A choice-matrix with no columns or no
  // rows after normalisation is the same failure mode — see the field-types
  // spec §2's degradation table.
  const optionless = (field.type === 'radio' || field.type === 'checkbox-group' || field.type === 'pictorial-choice') && field.options.length === 0;
  const matrixUnanswerable = field.type === 'choice-matrix' && (field.columns.length === 0 || field.rows.length === 0);
  const asFreeText = !field.knownType || optionless || matrixUnanswerable;

  return (
    <div style={{ marginBottom: 12 }}>
      <label className="lbl" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>{field.label || field.key}</span>
        {(field.type === 'text' || asFreeText) && <VoiceMicButton onText={t => onChange(((value as string) ? value + ' ' : '') + t)} />}
      </label>
      {asFreeText && (
        <>
          <input value={(value as string) || ''} onChange={e => onChange(e.target.value)} />
          <div style={{ fontSize: 10.5, color: 'var(--color-ink-muted)', marginTop: 3 }}>
            {(optionless || matrixUnanswerable) ? 'This question has no answer options configured — recorded as free text.'
                        : `Unrecognised field type "${field.type}" — recorded as free text.`}
          </div>
        </>
      )}
      {!asFreeText && field.type === 'radio' && (
        <div style={{ marginTop: 4 }}>
          <SegmentedControl
            size="sm"
            options={field.options.map(opt => ({ value: opt, label: opt }))}
            value={value as string}
            onChange={onChange}
            disabled={disabled}
          />
        </div>
      )}
      {!asFreeText && field.type === 'checkbox-group' && (
        <div style={{ marginTop: 4 }}>
          <SegmentedControl
            multiple
            size="sm"
            options={field.options.map(opt => ({ value: opt, label: opt }))}
            value={(value as string[]) || []}
            onChange={onChange}
            disabled={disabled}
          />
        </div>
      )}
      {!asFreeText && field.type === 'text' && (
        <input value={(value as string) || ''} onChange={e => onChange(e.target.value)} />
      )}
      {!asFreeText && field.type === 'number' && (
        // asOfDate is "now" — this component only ever renders as a live
        // entry form (its one caller, PreConsultStage.tsx, has no saved
        // completion timestamp to review against), so a value on screen was
        // just typed and its recency Badge should say so.
        <NumericField field={field} value={value as string | undefined} onChange={onChange} trend={trend} asOfDate={new Date().toISOString()} />
      )}
      {field.type === 'vitals' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 4 }}>
          {(field.options.length ? field.options : field.items.map(i => i.label)).map(opt => (
            <div key={opt}>
              <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 2 }}>{opt}</div>
              <input value={(value as Record<string, string> | undefined)?.[opt] || ''} onChange={e => onChange({ ...(value as Record<string, string> || {}), [opt]: e.target.value })} style={{ width: 80 }} />
            </div>
          ))}
        </div>
      )}
      {/* Two-column screening table + Done/Due/Pending donut — see
          DueChecklistField's own doc comment. */}
      {field.type === 'due-checklist' && (
        <DueChecklistField field={field} value={value} onChange={onChange} disabled={disabled} />
      )}

      {/* Per-condition No/Yes/Unknown status — deliberately not a
          checkbox-group (that only captures "present or not", with no way to
          record that it hasn't actually been asked yet). Value shape:
          {[itemLabel]: 'No'|'Yes'|'Unknown'}. Fixed colours (green/red/amber),
          not admin-configurable per item, since the meaning of each state is
          the same across every condition-status field a template might add. */}
      {field.type === 'condition-status' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
          {field.items.map(item => {
            const state = (value as Record<string, string> | undefined)?.[item.label] as ConditionStatus | undefined;
            return (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12.5, minWidth: 140 }}>{item.label}</span>
                <SegmentedControl
                  size="sm"
                  options={CONDITION_STATUS_OPTIONS}
                  value={state as ConditionStatus}
                  onChange={v => onChange({ ...(value as Record<string, string> || {}), [item.label]: v })}
                  disabled={disabled}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* One question asked identically of several rows on one shared scale —
          see docs/HMS_V4_ASSESSMENT_FIELD_TYPES_SPEC.md §2. Value shape:
          {[rowKey]: cell.option}. */}
      {!asFreeText && field.type === 'choice-matrix' && (
        <div style={{ marginTop: 4 }}>
          <ChoiceMatrix
            columns={field.columns}
            rows={field.rows}
            value={value as Record<string, string> | undefined}
            onChange={onChange}
            disabled={disabled}
            footnote={field.footnote}
          />
        </div>
      )}

      {/* A single-answer question whose options are better recognised than
          read — see docs/HMS_V4_ASSESSMENT_FIELD_TYPES_SPEC.md §3. Stored
          answer is the option's value, identical to `radio`. */}
      {!asFreeText && field.type === 'pictorial-choice' && (
        <div style={{ marginTop: 4 }}>
          <PictorialChoiceList
            options={field.pictorialOptions}
            value={value as string | undefined}
            onChange={onChange}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}

// Sequential A, B, C... letters for whatever sections actually render, in
// display order — not a stable field identity. Reordering sections in the
// Admin Panel template builder relabels these along with it, which is a
// deliberate, stated trade-off (see the assessment's decision log item 4):
// the letters are for "which card are we talking about" in a conversation
// between staff, not something anything downstream depends on.
// Spreadsheet-style, so a template with more than 26 sections keeps producing
// letters (…Y, Z, AA, AB) instead of running off the end of the alphabet into
// punctuation — an Admin Panel edit can add sections without limit.
export function letterAt(i: number): string {
  let n = i;
  let out = '';
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

// Some existing templates (e.g. seed_diabetes_module.js's "A. DIABETES
// STATUS") were authored with a letter baked into the section label itself,
// predating this component's own letter badge. Strip it for display only —
// the stored label is untouched — so a template written either way shows
// exactly one letter, not two disagreeing ones.
export function stripLeadingLetter(label: string): string {
  return label.replace(/^[A-Za-z]\.\s*/, '');
}

const isEmptyValue = (v: unknown) => v === '' || v == null || (Array.isArray(v) && v.length === 0) || (typeof v === 'object' && !Array.isArray(v) && Object.keys(v as object).length === 0);

export function fieldsAnsweredCount(fields: NormalisedField[], answers: Record<string, FieldValue>): { answered: number; total: number } {
  const relevant = fields.filter(f => f.type !== 'history-table');
  return { answered: relevant.filter(f => !isEmptyValue(answers[f.key])).length, total: relevant.length };
}
export function statusFromCount(answered: number, total: number): SectionStatus {
  if (total === 0 || answered === 0) return 'incomplete';
  return answered === total ? 'complete' : 'partial';
}

/**
 * The self-contained editor for one ailment template's dynamic sections —
 * everything after "Triage" in Pre-Consult's own section grid. Renders each
 * visible section as a lettered SectionCard of TemplateFields, and owns the
 * one thing PreConsultStage.tsx used to fetch itself: prior readings for
 * every numeric field, batched into one request per template rather than one
 * per field, silent on failure (a trend is context — a screen that can't
 * fetch history must still let a reading be recorded/corrected).
 *
 * Used both by PreConsultStage.tsx (the nurse's original entry) and
 * ConsultStage.tsx's Pre-Consult correction form (the doctor's in-place
 * amend) — the same component, not a lookalike, so the two can never
 * silently render a field differently.
 */
export function AilmentAssessmentEditor({
  templateId, templateSections, answers, onChange, patientUhid, letterOffset = 0, disabled, historyRefreshToken,
}: {
  templateId: number;
  templateSections: NormalisedSection[];
  answers: Record<string, FieldValue>;
  onChange: (key: string, value: FieldValue) => void;
  patientUhid: string | undefined;
  /** SectionCard index letters continue from here — e.g. Pre-Consult's own
   * Triage section is "A", so its dynamic sections start at offset 1 (B, C,
   * D…). A caller with no lettered section before this one passes 0. */
  letterOffset?: number;
  /** See TemplateField's own doc comment. */
  disabled?: boolean;
  /** See TemplateField's own doc comment. */
  historyRefreshToken?: unknown;
}) {
  const [trends, setTrends] = useState<Record<string, TrendPoint[]>>({});

  useEffect(() => {
    const numericKeys = templateSections.flatMap(sec => sec.fields).filter(f => f.type === 'number').map(f => f.key);
    if (!numericKeys.length || !patientUhid) { setTrends({}); return; }
    let cancelled = false;
    getAssessmentTrend(patientUhid, templateId, numericKeys)
      .then((res: Record<string, TrendPoint[]>) => { if (!cancelled) setTrends(res || {}); })
      .catch(() => { if (!cancelled) setTrends({}); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, patientUhid]);

  const visibleSections = templateSections
    .map(sec => ({ sec, visibleFields: sec.fields.filter(f => isFieldVisible(f, answers)) }))
    .filter(({ visibleFields }) => visibleFields.length > 0);

  return (
    <>
      {visibleSections.map(({ sec, visibleFields }, i) => {
        const { answered, total } = fieldsAnsweredCount(visibleFields, answers);
        return (
          <SectionCard key={i} index={letterAt(i + letterOffset)} title={sec.label ? stripLeadingLetter(sec.label) : ''} status={statusFromCount(answered, total)} span={sec.span}>
            {visibleFields.map(field => (
              <TemplateField key={field.key} field={field} value={answers[field.key]} onChange={v => onChange(field.key, v)} patientUhid={patientUhid} trend={trends[field.key]} disabled={disabled} historyRefreshToken={historyRefreshToken} />
            ))}
          </SectionCard>
        );
      })}
    </>
  );
}
